import React from 'react';
import { blurOnWheel } from '../../lib/number-input';
import type { EquipmentUsageRow } from '../../lib/equipment-usage';

/**
 * Hours and runs for one used asset on a ticket (api#978, decision D3).
 *
 * The hours box starts EMPTY with the ticket's hours as its placeholder: empty
 * means "the ticket's hours", which the API fills in. Only a typed number is
 * the crew's. Never prefill the box with the default — a default shown as a
 * value is indistinguishable from one the hand typed, and would be sent back
 * as theirs.
 *
 * Shared by the field and office ticket editors so the two cannot disagree.
 */
export default function EquipmentHoursRuns({
  item,
  ticketHours,
  onChange,
}: {
  item: EquipmentUsageRow;
  /** The ticket's hours, formatted ("8.00"), or null when it has none. */
  ticketHours: string | null;
  onChange: (patch: Partial<Pick<EquipmentUsageRow, 'hoursText' | 'runsText'>>) => void;
}) {
  const who = item.name || 'Equipment';
  // Wraps rather than overflowing. Unwrapped, the row's minimum width was 344px
  // inside a 290px card at a 390px viewport, which scrolled the whole field
  // editor sideways by 20px. Below `sm` the indent under the equipment name is
  // dropped and the hint takes its own line; from `sm` up it reads as before.
  return (
    <div className="basis-full min-w-0 flex flex-wrap items-center gap-x-3 gap-y-2 sm:pl-12">
      <label className="flex items-center gap-1.5 text-xs text-fieldiq-muted">
        Hours
        <input
          type="number"
          inputMode="decimal"
          min={0}
          step="0.25"
          value={item.hoursText}
          placeholder={ticketHours ? `${ticketHours} (ticket)` : 'ticket'}
          aria-label={`${who} hours`}
          aria-describedby={`hrs-hint-${item.equipmentId}`}
          onWheel={blurOnWheel}
          onChange={(e) => onChange({ hoursText: e.target.value })}
          className="w-24 h-11 rounded-lg bg-fieldiq-dark border border-fieldiq-border px-2 text-sm text-white placeholder:text-fieldiq-muted/70"
        />
      </label>
      <label className="flex items-center gap-1.5 text-xs text-fieldiq-muted">
        Runs
        <input
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          value={item.runsText}
          placeholder="—"
          aria-label={`${who} runs`}
          onWheel={blurOnWheel}
          onChange={(e) => onChange({ runsText: e.target.value })}
          className="w-16 h-11 rounded-lg bg-fieldiq-dark border border-fieldiq-border px-2 text-sm text-white placeholder:text-fieldiq-muted/70"
        />
      </label>
      <span id={`hrs-hint-${item.equipmentId}`} className="basis-full sm:basis-auto min-w-0 text-[11px] text-fieldiq-muted/70">
        {/* An unsaved ticket has no server hours yet; the API fills the default on save. */}
        Leave hours empty to use the ticket’s hours
      </span>
    </div>
  );
}
